<template>
  <div class="system-logs p-4">
    <admin-nav />
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2>
        <i class="fas fa-file-alt me-2"></i>
        System Logs
      </h2>
      <button class="btn btn-primary" @click="refreshLogs">
        <i class="fas fa-sync-alt me-2"></i>
        Refresh
      </button>
    </div>

    <!-- Filters Card -->
    <div class="card mb-4">
      <div class="card-header">
        <h5 class="mb-0">
          <i class="fas fa-filter me-2"></i>
          Filters
        </h5>
      </div>
      <div class="card-body">
        <div class="row">
          <div class="col-md-3 mb-3">
            <label class="form-label">Time Range</label>
            <select class="form-select" v-model="filters.timeRange" @change="applyQuickFilter">
              <option value="1h">Last Hour</option>
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="custom">Custom Range</option>
            </select>
          </div>
          <div class="col-md-3 mb-3" v-if="filters.timeRange === 'custom'">
            <label class="form-label">Start Date</label>
            <input type="datetime-local" class="form-control" v-model="filters.startTime">
          </div>
          <div class="col-md-3 mb-3" v-if="filters.timeRange === 'custom'">
            <label class="form-label">End Date</label>
            <input type="datetime-local" class="form-control" v-model="filters.endTime">
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Log Level</label>
            <select class="form-select" v-model="filters.minLevel">
              <option :value="null">All Levels</option>
              <option value="0">Trace</option>
              <option value="1">Debug</option>
              <option value="2">Information</option>
              <option value="3">Warning</option>
              <option value="4">Error</option>
              <option value="5">Critical</option>
            </select>
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Category</label>
            <input type="text" class="form-control" v-model="filters.category" placeholder="e.g. Access, Auth">
          </div>
          <div class="col-md-3 mb-3">
            <label class="form-label">Search Message</label>
            <input type="text" class="form-control" v-model="filters.message" placeholder="Search in messages...">
          </div>
          <div class="col-md-12">
            <button class="btn btn-primary" @click="loadLogs">
              <i class="fas fa-search me-2"></i>
              Apply Filters
            </button>
            <button class="btn btn-secondary ms-2" @click="clearFilters">
              <i class="fas fa-times me-2"></i>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Stats -->
    <div class="row mb-4" v-if="stats">
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h6 class="text-muted">Total Logs</h6>
            <h3>{{ stats.totalLogs }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h6 class="text-muted text-danger">Errors</h6>
            <h3 class="text-danger">{{ stats.errors }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h6 class="text-muted text-warning">Warnings</h6>
            <h3 class="text-warning">{{ stats.warnings }}</h3>
          </div>
        </div>
      </div>
      <div class="col-md-3">
        <div class="card">
          <div class="card-body text-center">
            <h6 class="text-muted text-info">Info</h6>
            <h3 class="text-info">{{ stats.info }}</h3>
          </div>
        </div>
      </div>
    </div>

    <!-- Logs Table -->
    <div class="card">
      <div class="card-header">
        <h5 class="mb-0">Log Entries ({{ totalCount }})</h5>
      </div>
      <div class="card-body">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>
        
        <div v-else-if="logs.length === 0" class="text-center py-5 text-muted">
          <i class="fas fa-inbox fa-3x mb-3"></i>
          <p>No logs found matching the current filters.</p>
        </div>

        <div v-else class="table-responsive">
          <table class="table table-sm table-hover">
            <thead>
              <tr>
                <th style="width: 140px;">Timestamp</th>
                <th style="width: 80px;">Level</th>
                <th style="width: 120px;">Category</th>
                <th>Message</th>
                <th style="width: 80px;">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="log in logs" :key="log.Id || log.Timestamp" :class="getLogRowClass(log)">
                <td>
                  <small>{{ formatDate(log.Timestamp) }}</small>
                </td>
                <td>
                  <span class="badge" :class="getLevelBadgeClass(log.Level)">
                    {{ getLevelName(log.Level) }}
                  </span>
                </td>
                <td>
                  <small>{{ log.Category }}</small>
                </td>
                <td>
                  <div class="log-message">{{ log.Message }}</div>
                  <div v-if="log.Exception" class="text-danger small mt-1">
                    <i class="fas fa-exclamation-triangle me-1"></i>
                    Exception: {{ log.Exception.substring(0, 100) }}...
                  </div>
                </td>
                <td>
                  <button 
                    class="btn btn-sm btn-outline-primary" 
                    @click="viewDetails(log)"
                    title="View Details">
                    <i class="fas fa-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="d-flex justify-content-between align-items-center mt-3" v-if="totalCount > 0">
          <div>
            Showing {{ ((currentPage - 1) * pageSize) + 1 }} to {{ Math.min(currentPage * pageSize, totalCount) }} of {{ totalCount }} entries
          </div>
          <nav>
            <ul class="pagination mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <button class="page-link" @click="changePage(currentPage - 1)" :disabled="currentPage === 1">
                  Previous
                </button>
              </li>
              <li class="page-item" v-for="page in visiblePages" :key="page" :class="{ active: page === currentPage }">
                <button class="page-link" @click="changePage(page)">{{ page }}</button>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <button class="page-link" @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">
                  Next
                </button>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>

    <!-- Log Details Modal -->
    <div class="modal" :class="{ 'show d-block': showDetailsModal }" v-if="showDetailsModal">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Log Details</h5>
            <button type="button" class="btn-close" @click="showDetailsModal = false"></button>
          </div>
          <div class="modal-body" v-if="selectedLog">
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>Timestamp:</strong><br>
                {{ formatDateFull(selectedLog.Timestamp) }}
              </div>
              <div class="col-md-6">
                <strong>Level:</strong><br>
                <span class="badge" :class="getLevelBadgeClass(selectedLog.Level)">
                  {{ getLevelName(selectedLog.Level) }}
                </span>
              </div>
            </div>
            <div class="row mb-3">
              <div class="col-md-6">
                <strong>Category:</strong><br>
                {{ selectedLog.Category }}
              </div>
              <div class="col-md-6" v-if="selectedLog.RequestId">
                <strong>Request ID:</strong><br>
                <code>{{ selectedLog.RequestId }}</code>
              </div>
            </div>
            <div class="mb-3">
              <strong>Message:</strong><br>
              <pre class="bg-light p-3 rounded">{{ selectedLog.Message }}</pre>
            </div>
            <div class="mb-3" v-if="selectedLog.Exception">
              <strong>Exception:</strong><br>
              <pre class="bg-danger text-white p-3 rounded">{{ selectedLog.Exception }}</pre>
            </div>
            <div class="row mb-3" v-if="getLogUserId(selectedLog) || getLogRoles(selectedLog)">
              <div class="col-md-6" v-if="getLogUserId(selectedLog)">
                <strong>User ID:</strong><br>
                <code>{{ getLogUserId(selectedLog) }}</code>
              </div>
              <div class="col-md-6" v-if="getLogRoles(selectedLog)">
                <strong>Roles:</strong><br>
                <span v-for="(role, idx) in parseRoles(getLogRoles(selectedLog))" :key="idx" class="badge bg-primary me-1">{{ role }}</span>
                <span v-if="!parseRoles(getLogRoles(selectedLog)).length" class="text-muted">None</span>
              </div>
            </div>
            <div class="row" v-if="selectedLog.Method || selectedLog.Path">
              <div class="col-md-4" v-if="selectedLog.Method">
                <strong>HTTP Method:</strong><br>
                {{ selectedLog.Method }}
              </div>
              <div class="col-md-4" v-if="selectedLog.Path">
                <strong>Path:</strong><br>
                {{ selectedLog.Path }}
              </div>
              <div class="col-md-4" v-if="selectedLog.StatusCode">
                <strong>Status Code:</strong><br>
                <span :class="getStatusClass(selectedLog.StatusCode)">{{ selectedLog.StatusCode }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDetailsModal = false">Close</button>
          </div>
        </div>
      </div>
    </div>
    <div class="modal-backdrop fade show" v-if="showDetailsModal"></div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import ApiService from '@/services/ApiService';
import AdminNav from '@/components/Shared/AdminNav.vue';

const logs = ref<any[]>([]);
const loading = ref(false);
const currentPage = ref(1);
const pageSize = ref(100);
const totalCount = ref(0);
const stats = ref<any>(null);
const showDetailsModal = ref(false);
const selectedLog = ref<any>(null);

const filters = ref({
  timeRange: '24h',
  startTime: '',
  endTime: '',
  minLevel: null as number | null,
  category: '',
  message: ''
});

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize.value));

const visiblePages = computed(() => {
  const pages: number[] = [];
  const maxVisible = 5;
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2));
  let end = Math.min(totalPages.value, start + maxVisible - 1);
  
  if (end - start < maxVisible - 1) {
    start = Math.max(1, end - maxVisible + 1);
  }
  
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }
  return pages;
});

onMounted(() => {
  loadLogs();
  loadStats();
});

const applyQuickFilter = () => {
  const now = new Date();
  
  switch (filters.value.timeRange) {
    case '1h':
      filters.value.endTime = now.toISOString();
      filters.value.startTime = new Date(now.getTime() - 3600000).toISOString();
      break;
    case '24h':
      filters.value.endTime = now.toISOString();
      filters.value.startTime = new Date(now.getTime() - 86400000).toISOString();
      break;
    case '7d':
      filters.value.endTime = now.toISOString();
      filters.value.startTime = new Date(now.getTime() - 604800000).toISOString();
      break;
    case '30d':
      filters.value.endTime = now.toISOString();
      filters.value.startTime = new Date(now.getTime() - 2592000000).toISOString();
      break;
  }
  
  if (filters.value.timeRange !== 'custom') {
    loadLogs();
  }
};

const loadLogs = async () => {
  loading.value = true;
  try {
    const params: any = {
      page: currentPage.value,
      pageSize: pageSize.value
    };
    
    if (filters.value.startTime) params.startTime = filters.value.startTime;
    if (filters.value.endTime) params.endTime = filters.value.endTime;
    if (filters.value.minLevel !== null) params.minLevel = filters.value.minLevel;
    if (filters.value.category) params.category = filters.value.category;
    if (filters.value.message) params.message = filters.value.message;
    
    const response = await ApiService.loadSystemLogs(params, true);
    logs.value = response.Data?.Logs || response.Data?.logs || [];
    totalCount.value = response.Data?.TotalCount || response.Data?.totalCount || 0;
  } catch (error) {
    console.error('Error loading logs:', error);
    alert('Failed to load logs');
  } finally {
    loading.value = false;
  }
};

const loadStats = async () => {
  try {
    const data = await ApiService.loadLogStats(true);
    
    stats.value = {
      totalLogs: data?.TotalLogs || data?.totalLogs || 0,
      errors: data?.LogLevels?.Error || data?.logLevels?.Error || 0,
      warnings: data?.LogLevels?.Warning || data?.logLevels?.Warning || 0,
      info: data?.LogLevels?.Information || data?.logLevels?.Information || 0
    };
  } catch (error) {
    console.error('Error loading log stats:', error);
  }
};

const refreshLogs = () => {
  loadLogs();
  loadStats();
};

const clearFilters = () => {
  filters.value = {
    timeRange: '24h',
    startTime: '',
    endTime: '',
    minLevel: null,
    category: '',
    message: ''
  };
  currentPage.value = 1;
  applyQuickFilter();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    loadLogs();
  }
};

const viewDetails = (log: any) => {
  selectedLog.value = log;
  showDetailsModal.value = true;
};

const getLevelName = (level: number): string => {
  const levels = ['Trace', 'Debug', 'Information', 'Warning', 'Error', 'Critical'];
  return levels[level] || 'Unknown';
};

const getLevelBadgeClass = (level: number): string => {
  const classes: { [key: number]: string } = {
    0: 'bg-secondary',
    1: 'bg-secondary',
    2: 'bg-info',
    3: 'bg-warning',
    4: 'bg-danger',
    5: 'bg-danger'
  };
  return classes[level] || 'bg-secondary';
};

const getLogRowClass = (log: any): string => {
  if (log.Level >= 4) return 'table-danger';
  if (log.Level === 3) return 'table-warning';
  return '';
};

const getStatusClass = (status: number): string => {
  if (status >= 500) return 'text-danger fw-bold';
  if (status >= 400) return 'text-warning fw-bold';
  if (status >= 300) return 'text-info';
  return 'text-success';
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatDateFull = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short'
  });
};

const parseRoles = (roles: string | string[] | null): string[] => {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles;
  if (typeof roles === 'string') {
    try {
      const parsed = JSON.parse(roles);
      return Array.isArray(parsed) ? parsed : [roles];
    } catch {
      return roles.split(',').map(r => r.trim()).filter(r => r);
    }
  }
  return [];
};

// Helper functions to handle both camelCase and PascalCase property names
const getLogUserId = (log: any): string | null => {
  return log?.UserId || log?.userId || null;
};

const getLogRoles = (log: any): string | null => {
  return log?.Roles || log?.roles || null;
};
</script>

<style scoped>
.log-message {
  max-width: 600px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  margin-bottom: 0;
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

.table-responsive {
  max-height: 600px;
  overflow-y: auto;
}
</style>
